import { createElement, replaceWith } from "../utils";

import Disclosure from "./Disclosure";

/**
 * An accordion component.
 *
 * This component should include the attributes and behaviors described in the
 * {@link https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/|WAI ARIA APG Accordion Example}.
 *
 * Accordion items to be specified as child `<details>` elements.
 *
 * @example
 * ```html
 * <ifrs-accordion>
 *   <details>
 *     <summary>Accordion item 1</summary>
 *     <p>Lorem ipsum...</p>
 *     <p>Lorem ipsum...</p>
 *   </details>
 *   <details>
 *     <summary>Accordion item 2</summary>
 *     Lorem ipsum...
 *   </details>
 * </ifrs-accordion>
 * ```
 */
export default class Accordion extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-accordion") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    // Run setup again when children change
    const observer = new MutationObserver(() => this.setup());
    observer.observe(this, { childList: true });
    this.cleanup.add(() => observer.disconnect());

    /**
     * Unless the `multiple` attribute is present, no more than one accordion
     * item may be expanded at a time. This event handler reacts to "toggle"
     * events emitted by child disclosure elements, collapsing inactive items
     * as necessary.
     *
     * @param {Event} event A toggle event (from disclosure elements).
     */
    const handleToggle = (event) => {
      if (
        !this.hasAttribute("multiple") &&
        event.target instanceof Disclosure &&
        event.target.open
      ) {
        // Close all other open disclosure elements
        this.querySelectorAll("ifrs-disclosure[open]").forEach((el) => {
          if (el instanceof Disclosure && el !== event.target) {
            el.open = false;
          }
        });
      }
    };

    this.addEventListener("toggle", handleToggle);
    this.cleanup.add(() => this.removeEventListener("toggle", handleToggle));
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    this.querySelectorAll("details").forEach((details) => {
      replaceWith(
        details,
        createElement("ifrs-disclosure", {
          classList: "accordion-item",
          innerHTML: details.outerHTML,
        }),
      );
    });
  }
}
